import { useParams } from 'react-router-dom'
import { Badge, Card, CardBody, CardHeader, Skeleton } from '@/components/ui'
import { AttributionNotice, PlatformIcon, QueryBoundary, SpeakerChip } from '@/components/domain'
import { formatDayLabel, formatDuration, formatTime } from '@/internationalization/format'
import { useT } from '@/internationalization/i18n'
import { useSharedSummary } from './api'

/**
 * A meeting summary opened from a share link, by someone who may not have an
 * account.
 *
 * Read-only by construction: the endpoint returns a `SharedSummary`, which has
 * no transcript, no audio URL and no participant emails — so there is nothing
 * here to accidentally render. A share link is a bearer credential, and what it
 * can reach is the minimum that makes it useful.
 */
export function SharedSummaryPage() {
  const t = useT()
  const { token } = useParams<{ token: string }>()
  const { data, isPending, error, refetch } = useSharedSummary(token)

  return (
    <QueryBoundary
      isLoading={isPending}
      error={error}
      onRetry={refetch}
      skeleton={
        <div className="space-y-4">
          <Skeleton className="h-7 w-72" />
          <Skeleton shape="block" className="h-48 w-full" />
        </div>
      }
    >
      {data && (
        <article className="space-y-4">
          <header>
            <div className="mb-2 flex items-center gap-2">
              <PlatformIcon platform={data.platform} size="sm" />
              <Badge tone="neutral">{t('shared.badge')}</Badge>
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-fg">{data.title}</h1>
            <p className="mt-1 text-base text-fg-muted">
              {formatDayLabel(data.startsAt)} · {formatTime(data.startsAt)} ·{' '}
              {formatDuration(data.durationSeconds)} · {t('shared.participants', { count: data.participantNames.length })}
            </p>
          </header>

          <AttributionNotice check={data.clockCheck} />

          {data.summary ? (
            <>
              <Card>
                <CardHeader title={t('shared.discussion')} />
                <CardBody>
                  <p className="whitespace-pre-line text-base leading-relaxed text-fg">
                    {data.summary.narrative}
                  </p>
                </CardBody>
              </Card>

              <Card>
                <CardHeader title={t('shared.decisions')} />
                <CardBody className="space-y-3">
                  {data.summary.decisions.map((decision) => (
                    <div key={decision.id}>
                      <p className="text-base text-fg">{decision.text}</p>
                      {decision.decidedBy && <SpeakerChip name={decision.decidedBy} className="mt-1" />}
                    </div>
                  ))}
                </CardBody>
              </Card>

              <Card>
                <CardHeader title={t('shared.actionItems')} />
                <CardBody>
                  <ul className="space-y-2">
                    {data.actionItems.map((item) => (
                      <li key={item.id} className="flex flex-wrap items-center gap-2 text-base text-fg">
                        <span className="min-w-0 flex-1">{item.text}</span>
                        <Badge tone={item.ownerName ? (item.ownerInferred ? 'warning' : 'neutral') : 'warning'}>
                          {item.ownerName ?? t('shared.unassigned')}
                          {item.ownerInferred && t('shared.inferredSuffix')}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                </CardBody>
              </Card>
            </>
          ) : (
            <Card>
              <CardBody>{t('shared.noSummary')}</CardBody>
            </Card>
          )}

          <p className="pt-2 text-sm text-fg-subtle">
            {t('shared.footer')}
          </p>
        </article>
      )}
    </QueryBoundary>
  )
}
